import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ContextMenu, type ContextMenuItem } from './ContextMenu'
import { useTableEditorModal } from '@/hooks/useTableEditorModal'
import { morphToast } from '@/components/ui/Toast'
import { useDiagramStore, useSettingsStore, useViewportStore } from '@/stores'
import { useClipboardStore } from '@/stores/clipboardStore'

interface CanvasContextMenuProps {
  open: boolean
  x: number
  y: number
  canvasX: number
  canvasY: number
  onClose: () => void
  readOnly?: boolean
}

function getNextTableName(existingIds: string[]): string {
  const existing = new Set(existingIds)
  let index = 1

  while (existing.has(`table_${index}`)) {
    index += 1
  }

  return `table_${index}`
}

export function CanvasContextMenu({
  open,
  x,
  y,
  canvasX,
  canvasY,
  onClose,
  readOnly = false,
}: CanvasContextMenuProps) {
  const { t } = useTranslation()
  const tables = useDiagramStore((state) => state.tables)
  const autoLayout = useDiagramStore((state) => state.autoLayout)
  const showGrid = useSettingsStore((state) => state.showGrid)
  const toggleGrid = useSettingsStore((state) => state.toggleGrid)
  const resetView = useViewportStore((state) => state.resetView)
  const openCreate = useTableEditorModal((state) => state.openCreate)
  const hasCopied = useClipboardStore((state) => state.copiedTables.length > 0)
  const pasteTables = useClipboardStore((state) => state.pasteTables)

  const items = useMemo<ContextMenuItem[]>(() => [
    ...(!readOnly ? [{
      id: 'canvas-create-table',
      label: t('contextMenu.createTableHere'),
      icon: '＋',
      onSelect: () => {
        const name = getNextTableName(tables.map((table) => table.id))
        openCreate({
          position: {
            x: Math.round(canvasX),
            y: Math.round(canvasY),
          },
          suggestedName: name,
        })
      },
    } as ContextMenuItem,
    {
      id: 'canvas-paste-table',
      label: t('contextMenu.pasteTableHere'),
      icon: '⎘',
      shortcut: 'Ctrl+V',
      disabled: !hasCopied,
      onSelect: () => {
        pasteTables({ x: Math.round(canvasX), y: Math.round(canvasY) })
      },
    } as ContextMenuItem,
    {
      type: 'separator',
      id: 'canvas-separator-main',
    } as ContextMenuItem] : []),
    {
      id: 'canvas-fit-view',
      label: t('toolbar.zoomReset'),
      icon: '⌂',
      onSelect: () => resetView(useDiagramStore.getState().tables),
    },
    ...(!readOnly ? [{
      id: 'canvas-auto-layout',
      label: t('contextMenu.autoLayout'),
      icon: '⊹',
      disabled: tables.length === 0,
      onSelect: () => {
        autoLayout()
        requestAnimationFrame(() => resetView(useDiagramStore.getState().tables))
      },
    } as ContextMenuItem] : []),
    {
      id: 'canvas-toggle-grid',
      label: t('settingsMenu.grid'),
      icon: '⊞',
      onSelect: () => {
        toggleGrid()
        morphToast.success(t(showGrid ? 'prompts.gridOff' : 'prompts.gridOn'))
      },
    },
  ], [autoLayout, canvasX, canvasY, hasCopied, openCreate, pasteTables, readOnly, resetView, showGrid, t, tables, toggleGrid])

  return (
    <ContextMenu
      open={open}
      x={x}
      y={y}
      items={items}
      onClose={onClose}
    />
  )
}
